/**
 * components/admin/PolicyImagesSection.jsx
 * =========================================
 * Admin tab: every instructor-supplied policy reminder image, with takedown.
 *
 * Blocking requires a reason, and that reason is shown to the instructor in
 * their own Manage tab — so it should read as something you'd say to them.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import styles from './ManageTab.module.css';

export default function PolicyImagesSection() {
  const { apiFetch } = useAuth();

  const [images,  setImages]  = useState([]);
  const [loading, setLoading] = useState(false);
  const [blockId, setBlockId] = useState(null);   // instructor being blocked
  const [reason,  setReason]  = useState('');
  const [msg,     setMsg]     = useState(null);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/admin/policy-images');
      if (res.ok) setImages(await res.json());
    } catch { /* leave empty */ }
    finally { setLoading(false); }
  };

  const flash = (text, isError = false) => {
    setMsg({ text, isError });
    setTimeout(() => setMsg(null), 6000);
  };

  const doBlock = async (instructorId) => {
    if (!reason.trim()) { flash('A reason is required.', true); return; }
    try {
      const res = await apiFetch(`/api/admin/policy-images/${instructorId}/block`, {
        method: 'PUT',
        body: JSON.stringify({ reason: reason.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        flash(data.message);
        setBlockId(null);
        setReason('');
        await load();
      } else {
        flash(data.detail || 'Could not hide that image.', true);
      }
    } catch (err) { flash(err.message, true); }
  };

  const doUnblock = async (instructorId) => {
    try {
      const res = await apiFetch(`/api/admin/policy-images/${instructorId}/block`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (res.ok) { flash(data.message); await load(); }
      else flash(data.detail || 'Could not restore that image.', true);
    } catch (err) { flash(err.message, true); }
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Policy Reminder Images</h3>

      <p className={styles.policyHint}>
        Instructor-supplied images shown to students on a policy violation.
        Hiding one keeps the file and shows your reason to the instructor in
        their Manage tab.
      </p>

      {msg && <div className={msg.isError ? styles.error : styles.success}>{msg.text}</div>}

      {loading && <p className={styles.empty}>Loading…</p>}
      {!loading && images.length === 0 && (
        <p className={styles.empty}>No instructor has uploaded one.</p>
      )}

      {images.map((img) => (
        <div key={img.instructor_id} className={styles.policyImageRow}>
          {/* Blocked images still render here: judging an appeal means seeing
              what was blocked. */}
          <img src={img.image_url} alt={img.image_alt || ''} className={styles.policyImageThumb} />

          <div className={styles.policyImageInfo}>
            <div className={styles.policyImageName}>
              {img.instructor}
              {img.blocked && <span className={styles.policyImageBadge}>Hidden</span>}
            </div>
            <div className={styles.policyHint}>{img.email}</div>
            <div className={styles.policyHint}>Alt text: {img.image_alt || <em>none</em>}</div>
            {img.blocked && (
              <div className={styles.policyHint}>Reason given: {img.blocked_reason}</div>
            )}
          </div>

          <div className={styles.policyImageActions}>
            {img.blocked ? (
              <button className="btn-ghost" onClick={() => doUnblock(img.instructor_id)}>
                Restore
              </button>
            ) : blockId === img.instructor_id ? (
              <div className={styles.policyImageBlockForm}>
                <input
                  type="text"
                  value={reason}
                  maxLength={300}
                  autoFocus
                  placeholder="Reason — the instructor reads this"
                  onChange={(e) => setReason(e.target.value)}
                />
                <button className="btn-primary" onClick={() => doBlock(img.instructor_id)}>
                  Hide it
                </button>
                <button
                  className="btn-ghost"
                  onClick={() => { setBlockId(null); setReason(''); }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button className="btn-ghost" onClick={() => setBlockId(img.instructor_id)}>
                Hide from students
              </button>
            )}
          </div>
        </div>
      ))}
    </section>
  );
}
